/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { assetImages } from "constants";
import PatientDetailsCard from "../PatientDetailsCard/PatientDetailsCard";

const SaveOxygenSaturationPopup = ({
  patientObj = null,
  afterActionClose = () => {},
  selectedOxygenSaturationId = null, // Default value for selectedOxygenSaturationId
  setSelectedOxygenSaturationId = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const userObj = {
    label: `${currentUser.name ?? currentUser.email} ${
      currentUser.surname ?? ""
    }`,
    value: currentUser._id,
  };

  // States for input fields
  const [oxygenSaturation, setOxygenSaturation] = useState("");
  const [oxygenSaturationDate, setOxygenSaturationDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Default to today's date
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading oxygen saturation details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!oxygenSaturation) {
      newErrors.oxygenSaturation = t("Please enter oxygen saturation value!");
    } else if (
      isNaN(oxygenSaturation) ||
      oxygenSaturation < 0 ||
      oxygenSaturation > 100
    ) {
      newErrors.oxygenSaturation = t(
        "Please enter a valid oxygen saturation percentage (0-100)!"
      );
    }
    if (!oxygenSaturationDate) {
      // Validation for new date field
      newErrors.oxygenSaturationDate = t("Please enter a date!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  //function for get oxygen saturation details
  const getOxygenSaturationDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_OXYGEN_SATURATION_BY_ID +
        `/${selectedOxygenSaturationId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const oxygenSaturationData = response.data;

        setOxygenSaturation(oxygenSaturationData.spo2);
        setOxygenSaturationDate(
          oxygenSaturationData.date
            ? new Date(oxygenSaturationData.date).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0]
        ); // Set date from API data, default to today's date if no date is available
        setAssignTo(oxygenSaturationData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveOxygenSaturationHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        spo2: parseInt(oxygenSaturation),
        date: oxygenSaturationDate, // Include date in API data
        user: assignTo ? assignTo?.value : null,
      };

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedOxygenSaturationId) {
        requestUrl +=
          url.API_UPDATE_OXYGEN_SATURATION +
          `/${selectedOxygenSaturationId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_OXYGEN_SATURATION + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#saveOxygenSaturationOffCanvas")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving oxygen saturation:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setOxygenSaturation("");
    setOxygenSaturationDate(new Date().toISOString().split("T")[0]); // Reset to today's date
    setAssignTo(patientObj);
    setErrors({});
    setSelectedOxygenSaturationId(null);
  };

  useEffect(() => {
    if (patientObj && !selectedOxygenSaturationId) {
      setAssignTo(patientObj);
    }
  }, [patientObj, selectedOxygenSaturationId]);

  useEffect(() => {
    if (selectedOxygenSaturationId) {
      getOxygenSaturationDetails();
    }
  }, [selectedOxygenSaturationId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveOxygenSaturationOffCanvas"
      aria-labelledby="saveOxygenSaturationOffCanvas"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              bloodtype
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="saveOxygenSaturationOffCanvas"
            >
              {selectedOxygenSaturationId
                ? t("Edit Oxygen Saturation")
                : t("Save Oxygen Saturation")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedOxygenSaturationId
                ? t("Manage existing oxygen saturation details")
                : t("Enter and save oxygen saturation readings")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading oxygen saturation details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading oxygen saturation details...")}
            </p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="d-flex flex-column h-100"
            >
              <div className="fields-container flex-fill">
                {/* Oxygen Saturation and Date on the same line */}
                <div className="row">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="oxygenSaturation"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Oxygen Saturation (%) *
                      </label>
                      <input
                        type="number"
                        id="oxygenSaturation"
                        className={`form-control fs-sm shadow-none ${
                          errors.oxygenSaturation
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 98"
                        value={oxygenSaturation}
                        onChange={(e) => {
                          setOxygenSaturation(e.target.value);
                          if (errors.oxygenSaturation) {
                            setErrors((prev) => ({
                              ...prev,
                              oxygenSaturation: "",
                            }));
                          }
                        }}
                      />
                      {errors.oxygenSaturation && (
                        <div className="invalid-feedback">
                          {errors.oxygenSaturation}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Date input */}
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="oxygenSaturationDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Date *
                      </label>
                      <input
                        type="date"
                        id="oxygenSaturationDate"
                        className={`form-control fs-sm shadow-none ${
                          errors.oxygenSaturationDate
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        value={oxygenSaturationDate}
                        onChange={(e) => {
                          setOxygenSaturationDate(e.target.value);
                          if (errors.oxygenSaturationDate) {
                            setErrors((prev) => ({
                              ...prev,
                              oxygenSaturationDate: "",
                            }));
                          }
                        }}
                      />
                      {errors.oxygenSaturationDate && (
                        <div className="invalid-feedback">
                          {errors.oxygenSaturationDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assign To field */}
                <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />
                  {assignTo ? <PatientDetailsCard patient={assignTo} /> : null}
                </div>
              </div>

              {/* Oxygen Saturation Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-symbols-outlined text-primary">
                        assessment
                      </span>
                      Oxygen Saturation Interpretation
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Reference ranges for adults
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Normal
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Oxygen Saturation:</strong> 95-100%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined fs-6">info</span>
                    Consult your healthcare provider for proper interpretation
                    and treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveOxygenSaturationHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Oxygen Saturation")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveOxygenSaturationPopup;
